import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useLocale } from 'next-intl'
import { useIpLocationStore } from 'store/server-context/ip-location'
import { useSessionStore } from 'store/server-context/session'
import { NO_MORE_DATA, TEMP_USER_ID } from 'constant'
import { searchSpotV2 } from 'api/search'
import { clientFetch } from 'lib/fetch/client'
import { getCommentsV2 } from 'api/comment'
import { fetchCommentsDetail } from 'server-actions/fetch-comments'

function useSearchResultComments(queryKeywords: string) {
  const locale = useLocale()
  const session = useSessionStore(state => state.session)
  const ipLocation = useIpLocationStore(state => state.ipLocation)

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isInitialLoading,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['searchResultSpost', queryKeywords],
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      let lat = '0'
      let lng = '0'

      if (ipLocation) {
        const {
          ip_geo_point: { lat: ipLat, lng: ipLng }
        } = ipLocation
        lat = `${ipLat}`
        lng = `${ipLng}`
      }
      const searchParams = new URLSearchParams()
      searchParams.set('keyword', queryKeywords)
      searchParams.set(
        'user_id',
        session.guest ? TEMP_USER_ID : session.user_id
      )
      searchParams.set('lat', lat)
      searchParams.set('lng', lng)
      searchParams.set('country_code', ipLocation?.country_code || '')
      searchParams.set('lang', locale)
      searchParams.set('size', '16')
      if (pageParam) {
        searchParams.set('page_token', pageParam)
      }

      try {
        const { data } = await searchSpotV2(clientFetch, searchParams)
        const commentIds = data.spots.map(spot => spot.comment_id)

        const { data: commentsV2 } = await getCommentsV2(
          clientFetch,
          commentIds.join(),
          locale
        )

        const comments = await fetchCommentsDetail(commentsV2, locale)

        return {
          comments,
          pageToken: data?.next_page_token ?? ''
        }
      } catch (error) {
        console.log(error)
        return {
          comments: [],
          pageToken: NO_MORE_DATA
        }
      }
    },
    getNextPageParam: lastPage => {
      if (
        (lastPage.comments.length ?? 0) === 0 ||
        !lastPage.pageToken ||
        lastPage.pageToken === NO_MORE_DATA
      ) {
        return undefined
      }
      return lastPage.pageToken
    },
    select(data) {
      return {
        comments: data.pages.flatMap(item => item.comments)
      }
    }
  })

  const noMoreData = !isInitialLoading && !hasNextPage
  return {
    comments: data?.comments || [],
    isFetchingNextPage,
    noMoreData,
    fetchNextPage,
    isInitialLoading,
    isFetching
  }
}

export default useSearchResultComments
